import { retainSearchParams } from "@tanstack/react-router";
import { z } from "zod";

import { propertyTypeValues } from "./property-types.ts";

import type { AssetGrouping, AssetSort } from "@estate-platform/domain/asset-index";
import type { CalendarLayer } from "@estate-platform/domain/calendar";
import type { DocumentAudience } from "@estate-platform/domain/document-audience";
import type { ExpiryFilter } from "@estate-platform/domain/documents";
import type { DocumentType } from "@estate-platform/domain/files";
import type { InboxTab } from "@estate-platform/domain/notification-inbox";
import type { PeopleTab } from "@estate-platform/domain/people-index";
import type { SearchGroup } from "@estate-platform/domain/search";
import type { VaultItemType } from "@estate-platform/domain/vault";
import type { ListType } from "@estate-platform/domain/work";
import type { SearchMiddleware } from "@tanstack/react-router";

export const lenient = <Schema extends z.ZodType>(schema: Schema): z.ZodCatch<z.ZodOptional<Schema>> =>
  schema.optional().catch(undefined);

const isIdList = (value: string): boolean => value.split(",").every((id) => z.uuid().safeParse(id).success);

export const propertyFilterSearch = z.object({
  p: lenient(z.string().max(1000).refine(isIdList)),
});

interface PropertyFilter {
  readonly p?: string | undefined;
}

// WHY: moving between filtered pages keeps the filter in the URL, so no page loads unfiltered and then corrects itself.
export const retainPropertyFilter = <Search extends PropertyFilter>(): {
  readonly middlewares: SearchMiddleware<Search>[];
} => ({ middlewares: [retainSearchParams<Search>(["p"])] });

export const propertyFilterOf = (search: PropertyFilter): string | undefined => search.p;

export const withPropertyFilter = <Search extends object>(
  search: Search,
  p: string | undefined,
): Search & { readonly p: string | undefined } => ({ ...search, p });

export const todaySearch = propertyFilterSearch.extend({
  task: lenient(z.uuid()),
});

export const calendarViewValues = ["agenda", "day", "week", "month"] as const;
const calendarLayerValues = [
  "guestStays",
  "familyTravel",
  "vendorVisits",
  "tasks",
  "shifts",
] as const satisfies readonly CalendarLayer[];

const layerNames: ReadonlySet<string> = new Set(calendarLayerValues);

const isLayerList = (value: string): boolean => value.split(",").every((layer) => layerNames.has(layer));

const formatIn = (timeZone: string): Intl.DateTimeFormat => new Intl.DateTimeFormat("en", { timeZone });

const isKnownZone = (value: string): boolean => {
  try {
    formatIn(value);
  } catch {
    return false;
  }
  return true;
};

// WHY: the domain's zone schema pulls Temporal into the first load; Intl knows the same IANA names.
const isTimeZone = (value: string): boolean => /^[a-z]/iv.test(value) && isKnownZone(value);

export const calendarSearch = propertyFilterSearch.extend({
  view: lenient(z.enum(calendarViewValues)),
  date: lenient(z.iso.date()),
  layers: lenient(z.string().max(100).refine(isLayerList)),
  residents: lenient(z.string().max(1000).refine(isIdList)),
  people: lenient(z.string().max(1000).refine(isIdList)),
  zone: lenient(z.string().max(64).refine(isTimeZone)),
});

export type CalendarSearch = z.infer<typeof calendarSearch>;

const planningViewValues = ["week", "month"] as const;

export const planningSearch = propertyFilterSearch.extend({
  view: lenient(z.enum(planningViewValues)),
  date: lenient(z.iso.date()),
  people: lenient(z.string().max(1000).refine(isIdList)),
  team: lenient(z.literal(1)),
  tasks: lenient(z.literal(0)),
  task: lenient(z.uuid()),
});

export type PlanningSearch = z.infer<typeof planningSearch>;

export const eventSearch = z.object({
  at: lenient(z.iso.datetime({ offset: true })),
  checklist: lenient(z.literal("new")),
});

const peopleTabValues = ["residents", "staff", "vendors", "guests"] as const satisfies readonly PeopleTab[];

// WHY: People is not a property-context page (01-navigation), so its own property chip keeps `p` without retaining it elsewhere.
export const peopleSearch = propertyFilterSearch.extend({
  tab: lenient(z.enum(peopleTabValues)),
  query: lenient(z.string().max(200)),
  service: lenient(z.string().min(1).max(100)),
  access: lenient(z.enum(["account", "link", "none"])),
});

export type PeopleSearch = z.infer<typeof peopleSearch>;

export const personTabSchema = z.enum(["profile", "schedule", "work", "access"]);

export type PersonTab = z.infer<typeof personTabSchema>;

export const personSearch = z.object({ when: lenient(z.literal("past")) });

// WHY: a literal mirror of the domain's search groups; its runtime module would join the first load.
const searchGroupNames: ReadonlySet<string> = new Set([
  "task",
  "project",
  "property",
  "asset",
  "person",
  "document",
  "manual",
  "list",
  "event",
  "logbook",
  "vault",
] as const satisfies readonly SearchGroup[]);

const isGroupList = (value: string): boolean =>
  value.split(",").every((group) => searchGroupNames.has(group));

export const searchUpdatedValues = ["week", "month", "year"] as const;

export const searchSearch = propertyFilterSearch.extend({
  q: lenient(z.string().max(200)),
  group: lenient(z.string().max(200).refine(isGroupList)),
  updated: lenient(z.enum(searchUpdatedValues)),
  photos: lenient(z.boolean()),
  sort: lenient(z.enum(["relevance", "updated"])),
});

export type SearchPageSearch = z.infer<typeof searchSearch>;

export const queryOf = (search: { readonly q?: string | undefined }): string => search.q ?? "";

export const withQuery = <Search extends object>(
  search: Search,
  text: string,
): Search & { readonly q: string | undefined } => ({ ...search, q: text === "" ? undefined : text });

export const searchPageLink = (text: string, group?: string): SearchPageSearch => ({
  q: text,
  ...(group !== undefined && { group }),
});

export const propertiesSearch = z.object({
  sort: lenient(z.enum(["name", "open"])),
  type: lenient(z.enum(propertyTypeValues)),
  country: lenient(z.string().regex(/^[A-Z]{2}$/v)),
});

export type PropertiesSearch = z.infer<typeof propertiesSearch>;

export const propertyTabSchema = z.enum([
  "overview",
  "zones",
  "assets",
  "tasks",
  "calendar",
  "logbook",
  "documents",
  "manual",
  "people",
  "costs",
]);

export type PropertyTab = z.infer<typeof propertyTabSchema>;

export const listTypeValues = [
  "checklist",
  "inventory",
  "packing",
  "other",
] as const satisfies readonly ListType[];
export const listStatusValues = ["active", "completed", "archived"] as const;
const listCreateValues = ["checklist", "inventory"] as const;

export const listsSearch = propertyFilterSearch.extend({
  tab: lenient(z.enum(["lists", "templates"])),
  type: lenient(z.enum(listTypeValues)),
  status: lenient(z.enum(listStatusValues)),
  create: lenient(z.enum(listCreateValues)),
});

export type ListsSearch = z.infer<typeof listsSearch>;

export const logbookSearch = propertyFilterSearch.extend({
  author: lenient(z.uuid()),
  linked: lenient(z.uuid()),
  from: lenient(z.iso.date()),
  to: lenient(z.iso.date()),
  has: lenient(z.literal("photos")),
  query: lenient(z.string().trim().min(1).max(200)),
  create: lenient(z.enum(["entry", "photo"])),
});

export type LogbookSearch = z.infer<typeof logbookSearch>;

const kindKeyPattern = /^[a-z][\dA-Za-z]*$/v;

// WHY: type-only mirrors of the domain lists; the asset index module pulls the Temporal polyfill into the first load.
export const assetGroupings = [
  "none",
  "kind",
  "property",
  "zone",
] as const satisfies readonly AssetGrouping[];
export const assetSorts = ["name", "kind", "nextDue", "updated"] as const satisfies readonly AssetSort[];
const assetDisplays = ["list", "table", "gallery"] as const;

export const assetsSearch = propertyFilterSearch.extend({
  kind: lenient(z.string().max(64).regex(kindKeyPattern)),
  zone: lenient(z.uuid()),
  due: lenient(z.literal("soon")),
  warranty: lenient(z.literal("expiring")),
  sensitivity: lenient(z.enum(["normal", "confidential"])),
  group: lenient(z.enum(assetGroupings)),
  sort: lenient(z.enum(assetSorts)),
  display: lenient(z.enum(assetDisplays)),
  query: lenient(z.string().max(200)),
});

export type AssetsSearch = z.infer<typeof assetsSearch>;

export const newAssetSearch = propertyFilterSearch.extend({
  property: lenient(z.uuid()),
  zone: lenient(z.uuid()),
});

export const assetTabSchema = z.enum(["overview", "maintenance", "history", "documents"]);

export type AssetTab = z.infer<typeof assetTabSchema>;

// WHY: type-only mirrors of the domain lists; the documents module pulls the Temporal polyfill into the first load.
export const documentTypeValues = [
  "warranty",
  "insurance",
  "contract",
  "identity",
  "manual",
  "plan",
  "report",
  "invoice",
  "quote",
  "paymentConfirmation",
  "certificate",
  "photo",
  "other",
] as const satisfies readonly DocumentType[];
export const expiryFilterValues = [
  "expired",
  "next30",
  "next90",
  "none",
] as const satisfies readonly ExpiryFilter[];

export const documentsSearch = propertyFilterSearch.extend({
  type: lenient(z.enum(documentTypeValues)),
  expiring: lenient(z.enum(expiryFilterValues)),
  audience: lenient(
    z.enum(["owners", "managers", "staff", "casualStaff"] satisfies readonly DocumentAudience[]),
  ),
  linked: lenient(z.uuid()),
  query: lenient(z.string().max(200)),
});

export type DocumentsSearch = z.infer<typeof documentsSearch>;

// WHY: a type-only mirror of the domain's vault item types; its runtime module pulls the vault schemas into this chunk.
export const vaultTypeValues = [
  "alarm",
  "safe",
  "gate",
  "wifi",
  "keyBox",
  "account",
  "access",
  "other",
] as const satisfies readonly VaultItemType[];

// WHY: Vault is not a property-context page (01-navigation); its own property filter keeps `p` without retaining it.
const vaultTabValues = ["codes", "documents"] as const;

// WHY: `type` is a vault item type on Codes and a document type on Documents; switching tabs clears it.
export const vaultSearch = propertyFilterSearch.extend({
  tab: lenient(z.enum(vaultTabValues)),
  type: lenient(z.enum([...vaultTypeValues, ...documentTypeValues])),
});

// WHY: `reveal=1` from any link only focuses Reveal; a reveal on arrival needs the search answer's history state.
export const vaultItemSearch = z.object({ reveal: lenient(z.literal(1)) });

export type VaultSearch = z.infer<typeof vaultSearch>;
// WHY: a literal mirror of the domain's inbox tabs; its module pulls the Temporal polyfill into the router's schemas.
const inboxTabs = ["all", "mentions", "assigned", "unread"] as const satisfies readonly InboxTab[];

export const notificationsSearch = z.object({ tab: lenient(z.enum(inboxTabs)) });

// WHY: literal mirrors of the projects data module's tabs and sorts; that module pulls the domain runtime into the router's schemas.
const projectStatusTabs = ["active", "done", "cancelled"] as const;
export const projectSorts = ["targetEnd", "updated", "title"] as const;

export const projectsSearch = propertyFilterSearch.extend({
  status: lenient(z.enum(projectStatusTabs)),
  sort: lenient(z.enum(projectSorts)),
  create: lenient(z.enum(["project", "workRequest"])),
  property: lenient(z.uuid()),
});

export type ProjectsSearch = z.infer<typeof projectsSearch>;

export const projectTabSchema = z.enum([
  "overview",
  "timeline",
  "tasks",
  "money",
  "files",
  "discussion",
  "activity",
  "links",
]);

export type ProjectTab = z.infer<typeof projectTabSchema>;

// WHY: `add` opens the Money tab's add sheet for that kind (from the page's Add menu or a link); a staff link to it finds no Money tab.
export const projectMoneyKinds = ["quote", "invoice", "payment"] as const;

// WHY: `add=file` opens the Files tab's upload and `add=phase` the timeline's inline phase entry; `due=overdue` filters the Tasks tab (the Overdue tile).
const projectAddKinds = [...projectMoneyKinds, "file", "phase"] as const;

export const projectSearch = z.object({
  add: lenient(z.enum(projectAddKinds)),
  due: lenient(z.literal("overdue")),
});

// WHY: plain text here keeps the register's value lists out of the first load; contracts/register-search.ts reads them.
const registerText = lenient(z.string().max(32));

export const contractsSearch = propertyFilterSearch.extend({
  view: registerText,
  type: registerText,
  category: lenient(z.uuid()),
  status: registerText,
  essential: lenient(z.literal(true)),
  provider: lenient(z.uuid()),
  month: registerText,
  tab: lenient(z.literal("ended")),
  group: registerText,
  sort: registerText,
  create: lenient(z.literal("contract")),
});

export type ContractsSearch = z.infer<typeof contractsSearch>;

export const contractTabSchema = z.enum(["overview", "invoices", "files", "discussion", "activity"]);

export type ContractTab = z.infer<typeof contractTabSchema>;
